import { FC, ReactElement, useCallback, useEffect, useState, useRef } from 'react';
import { Box, Typography, Button, LinearProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useTranslation } from 'react-i18next';

const BLOCKED_EXTENSIONS = ['exe', 'msi', 'com', 'scr', 'bat', 'cmd', 'ps1', 'vbs', 'js', 'wsf'];
const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const isBlockedFile = (fileName: string): boolean => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    return BLOCKED_EXTENSIONS.includes(extension);
};

const isFileTooLarge = (file: File): boolean => {
    return file.size > MAX_FILE_SIZE_BYTES;
};

interface FileDropZoneProps {
    disabled: boolean;
    isUploading: boolean;
    uploadProgress: number;
    onFilesSelected: (files: File[]) => void;
}

export const FileDropZone: FC<FileDropZoneProps> = ({
    disabled,
    isUploading,
    uploadProgress,
    onFilesSelected,
}): ReactElement => {
    const { t } = useTranslation();
    const [isDragging, setIsDragging] = useState(false);
    const [blockedFilesError, setBlockedFilesError] = useState<string | null>(null);
    const [fileSizeError, setFileSizeError] = useState<string | null>(null);
    
    // Counter to track drag enter/leave events across child elements
    const dragCounter = useRef(0);
    const blockedErrorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const fileSizeErrorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (blockedErrorTimeoutRef.current) clearTimeout(blockedErrorTimeoutRef.current);
            if (fileSizeErrorTimeoutRef.current) clearTimeout(fileSizeErrorTimeoutRef.current);
        };
    }, []);

    const validateAndProcessFiles = useCallback((fileList: FileList | File[]) => {
        const fileArray = Array.from(fileList);
        const blockedFiles = fileArray.filter((file) => isBlockedFile(file.name));
        const tooLargeFiles = fileArray.filter((file) => !isBlockedFile(file.name) && isFileTooLarge(file));
        const allowedFiles = fileArray.filter((file) => !isBlockedFile(file.name) && !isFileTooLarge(file));

        if (blockedFiles.length > 0) {
            setBlockedFilesError(t('attachment_modal.blocked_file_error', 'For security reasons, Pinnacle does not support the upload of that file type.'));
            if (blockedErrorTimeoutRef.current) clearTimeout(blockedErrorTimeoutRef.current);
            blockedErrorTimeoutRef.current = setTimeout(() => setBlockedFilesError(null), 5000);
        }

        if (tooLargeFiles.length > 0) {
            setFileSizeError(t('attachment_modal.file_size_error', 'The maximum size supported is 20MB per file.'));
            if (fileSizeErrorTimeoutRef.current) clearTimeout(fileSizeErrorTimeoutRef.current);
            fileSizeErrorTimeoutRef.current = setTimeout(() => setFileSizeError(null), 5000);
        }

        if (allowedFiles.length > 0) {
            onFilesSelected(allowedFiles);
        }
    }, [onFilesSelected, t]);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (selectedFiles) {
            validateAndProcessFiles(selectedFiles);
        }
        event.target.value = '';
    };

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        if (!disabled && dragCounter.current === 1) {
            setIsDragging(true);
        }
    }, [disabled]);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current = 0;
        setIsDragging(false);

        if (disabled) return;

        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles && droppedFiles.length > 0) {
            validateAndProcessFiles(droppedFiles);
        }
    }, [disabled, validateAndProcessFiles]);

    return (
        <Box
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            sx={[{
                borderRadius: '8px',
                p: { xs: 2, sm: 3 },
                mb: 2,
                textAlign: 'center',
                transition: 'all 0.2s ease',
                minHeight: { xs: '120px', sm: '150px' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
            }, isDragging ? {
                border: {
                    border: '#1976d2'
                }
            } : {
                border: {
                    border: '#D0D5DD'
                }
            }, isDragging ? {
                backgroundColor: '#EFF6FF'
            } : {
                backgroundColor: '#F9FAFB'
            }, disabled ? {
                opacity: 0.6
            } : {
                opacity: 1
            }, disabled ? {
                pointerEvents: 'none'
            } : {
                pointerEvents: 'auto'
            }]}
        >
            <CloudUploadIcon
                sx={[{
                    fontSize: 36,
                    mb: 1
                }, isDragging ? {
                    color: '#1976d2'
                } : {
                    color: '#667085'
                }]}
            />
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#1A1A2E', mb: 0.5 }}>
                {t('attachment_modal.drag_drop', 'Drag & drop files here')}
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: '#667085', mb: 1.5 }}>
                {t('attachment_modal.or_browse', 'or click to browse')}
            </Typography>
            <Button
                variant="contained"
                component="label"
                disabled={disabled || isUploading}
                sx={{
                    backgroundColor: '#1A1A2E',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.8125rem',
                    borderRadius: '6px',
                    px: 2.5,
                    py: 0.75,
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: '#2D2D44',
                        boxShadow: 'none',
                    },
                }}
            >
                {t('attachment_modal.browse_files', 'Browse Files')}
                <input type="file" multiple hidden onChange={handleFileSelect} />
            </Button>
            {blockedFilesError && (
                <Typography sx={{ fontSize: '0.8125rem', color: '#DC2626', mt: 2, fontWeight: 500 }}>
                    {blockedFilesError}
                </Typography>
            )}
            {fileSizeError && (
                <Typography sx={{ fontSize: '0.8125rem', color: '#DC2626', mt: 2, fontWeight: 500 }}>
                    {fileSizeError}
                </Typography>
            )}
            {isUploading && (
                <Box sx={{ mt: 2, width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#667085' }}>
                            {t('attachment_modal.uploading', 'Uploading...')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#667085' }}>
                            {Math.min(uploadProgress, 100)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(uploadProgress, 100)}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#E5E7EB',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                backgroundColor: '#242C63',
                            },
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};
